import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { TrustBar } from "@/components/TrustBar";
import { Services } from "@/components/Services";
import { WhyChooseUs } from "@/components/WhyChooseUs";


import { Comparison } from "@/components/Comparison";
import { Testimonials } from "@/components/Testimonials";
import { NotForEveryone } from "@/components/NotForEveryone";
import { Guarantee } from "@/components/Guarantee";
import { FAQ } from "@/components/FAQ";
import { ServiceAreaMap } from "@/components/ServiceAreaMap";
import { GiftCards } from "@/components/GiftCards";
import { ReferFriend } from "@/components/ReferFriend";

import { CleanerSpotlight } from "@/components/CleanerSpotlight";

import { WhatToExpectTimeline } from "@/components/WhatToExpectTimeline";
import { FinalCTA } from "@/components/FinalCTA";
import { Footer } from "@/components/Footer";
import { StickyBanner } from "@/components/StickyBanner";

import { PricingCalculator } from "@/components/PricingCalculator";
import { SEO } from "@/components/SEO";
import { InternalLinks } from "@/components/InternalLinks";
import { TimeSavedCalculator } from "@/components/TimeSavedCalculator";
import { ChecklistVisualizer } from "@/components/ChecklistVisualizer";
import { useLocation } from "@/lib/locations";
import { useEffect, startTransition, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";

const LazyServices = lazy(() => import("@/components/Services").then(m => ({ default: m.Services })));
const LazyWhyChooseUs = lazy(() => import("@/components/WhyChooseUs").then(m => ({ default: m.WhyChooseUs })));


const LazyComparison = lazy(() => import("@/components/Comparison").then(m => ({ default: m.Comparison })));
const LazyTestimonials = lazy(() => import("@/components/Testimonials").then(m => ({ default: m.Testimonials })));
const LazyNotForEveryone = lazy(() => import("@/components/NotForEveryone").then(m => ({ default: m.NotForEveryone })));
const LazyGuarantee = lazy(() => import("@/components/Guarantee").then(m => ({ default: m.Guarantee })));
const LazyFAQ = lazy(() => import("@/components/FAQ").then(m => ({ default: m.FAQ })));
const LazyServiceAreaMap = lazy(() => import("@/components/ServiceAreaMap").then(m => ({ default: m.ServiceAreaMap })));
const LazyGiftCards = lazy(() => import("@/components/GiftCards").then(m => ({ default: m.GiftCards })));
const LazyReferFriend = lazy(() => import("@/components/ReferFriend").then(m => ({ default: m.ReferFriend })));

const LazyCleanerSpotlight = lazy(() => import("@/components/CleanerSpotlight").then(m => ({ default: m.CleanerSpotlight })));

const LazyWhatToExpectTimeline = lazy(() => import("@/components/WhatToExpectTimeline").then(m => ({ default: m.WhatToExpectTimeline })));
const LazyFinalCTA = lazy(() => import("@/components/FinalCTA").then(m => ({ default: m.FinalCTA })));

const LazyPricingCalculator = lazy(() => import("@/components/PricingCalculator").then(m => ({ default: m.PricingCalculator })));
const LazyInternalLinks = lazy(() => import("@/components/InternalLinks").then(m => ({ default: m.InternalLinks })));
const LazyTimeSavedCalculator = lazy(() => import("@/components/TimeSavedCalculator").then(m => ({ default: m.TimeSavedCalculator })));
const LazyChecklistVisualizer = lazy(() => import("@/components/ChecklistVisualizer").then(m => ({ default: m.ChecklistVisualizer })));
const LazyBeforeAfterGame = lazy(() => import("@/components/BeforeAfterGame").then(m => ({ default: m.BeforeAfterGame })));

const Index = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const langPref = localStorage.getItem("langPref");
    const browserLang = navigator.language.toLowerCase();
    
    // Instead of hardcoding "sacramento", use the current location slug if it's a valid city/region for the language,
    // or fallback to sacramento if needed. The simplest way is to use location.slug.
    const targetSlug = location.slug === 'sacramento-area' ? 'sacramento' : location.slug;

    if (langPref === "es" || (!langPref && browserLang.startsWith("es"))) {
      if (!langPref) localStorage.setItem("langPref", "es");
      startTransition(() => { navigate(`/es/${targetSlug}`, { replace: true }); });
    } else if (langPref === "ru" || (!langPref && browserLang.startsWith("ru"))) {
      if (!langPref) localStorage.setItem("langPref", "ru");
      startTransition(() => { navigate(`/ru/${targetSlug}`, { replace: true }); });
    } else if (langPref === "zh" || (!langPref && (browserLang.startsWith("zh")))) {
      if (!langPref) localStorage.setItem("langPref", "zh");
      startTransition(() => { navigate(`/zh/${targetSlug}`, { replace: true }); });
    }
  }, [navigate, location.slug]);

  return (
    <div className="flex min-h-screen flex-col">
      <SEO 
          title="Professional Home Cleaning Service | Uplevel Cleaning"
          description={`Uplevel Cleaning offers top-rated house cleaning in ${location.name} & more. Instant flat-rate pricing, 60-second booking, 200% satisfaction guarantee.`}
          keywords={`home cleaning ${location.name}, house cleaning ${location.name}, maid service ${location.name}, deep cleaning ${location.name}, move out cleaning ${location.name}, recurring cleaning ${location.name}, residential cleaning ${location.name}, uplevel cleaning`}
          url="https://www.uplevelcleaning.com/"
          alternateUrl={`https://www.uplevelcleaning.com/es/${location.slug === 'sacramento-area' ? 'sacramento' : location.slug}`}
          alternateLang="es-US"
          locationName={location.name}
          regionSlug={location.regionSlug}
          includeLocalBusiness={true}
          includeFAQ={true}
          breadcrumbs={[{ name: "Home", url: "/" }]}
          reviews={[
            { author: "Mindi M.", rating: 5, reviewBody: "I had a great experience with Uplevel Cleaning. The service was prompt and the quality of the work was excellent. My house has never looked better!", datePublished: "2026-03-15" },
            { author: "Roberto N.", rating: 5, reviewBody: "A friend recommended I try Uplevel Cleaning and I was super impressed. They handled the deep clean of my new home perfectly.", datePublished: "2026-02-28" },
            { author: "Don J. B.", rating: 5, reviewBody: "In a word, perfect. The Uplevel team arrived on time and ready to go. Quick, thorough, and very professional. Best in Sacramento!", datePublished: "2026-01-20" }
          ]}
        />
      <StickyBanner />
      <Navbar />
      <main className="flex-1">
        <Hero />
        <TrustBar />
        <Suspense fallback={<div className="min-h-[200px] animate-pulse bg-muted/20" />}>
          <div className="content-auto">

            <LazyServices />
          </div>
        </Suspense>
        
        <Suspense fallback={<div className="min-h-[200px]" />}>
          <div className="content-auto">
            <LazyPricingCalculator />
            <LazyWhyChooseUs />

          </div>
        </Suspense>

        <Suspense fallback={<div className="min-h-[200px]" />}>
          <div className="content-auto">
            <LazyServiceAreaMap />
            <LazyTestimonials />

            <LazyComparison />
          </div>
        </Suspense>

        <Suspense fallback={<div className="min-h-[200px]" />}>
          <div className="content-auto">

            <LazyChecklistVisualizer />
            <LazyBeforeAfterGame />
            <LazyCleanerSpotlight />
          </div>
        </Suspense>

        <Suspense fallback={<div className="min-h-[200px]" />}>
          <div className="content-auto">
            <LazyNotForEveryone />
            <LazyGuarantee />
            <LazyReferFriend />
            <LazyGiftCards />
            <LazyTimeSavedCalculator />
          </div>
        </Suspense>
        
        <Suspense fallback={<div className="min-h-[200px]" />}>
          <div className="content-auto">
            <LazyFAQ />
            <LazyWhatToExpectTimeline />
            <LazyInternalLinks />
            <LazyFinalCTA />
          </div>
        </Suspense>

      </main>
      <Footer />
    </div>
  );
};

export default Index;
